<template>
  <slot v-if="responseStatus === undefined" name="loading"></slot>
  <slot v-else-if="responseStatus === 200" name="content" :data="responseData"></slot>
  <slot v-else name="error" :status="responseStatus"></slot>
</template>

<script setup>
import {onMounted, ref, watch} from "vue";
import axios from "axios";

const props = defineProps({
  endpoint: String,
  reload: Boolean,
  params: Object,
  cache: {
    type: Number,
    validator(value) {
      return value > 0;
    }
  }
});

const emit = defineEmits(["response", "update:reload", "update:loading"]);

const responseStatus = ref(undefined);
const responseData = ref(undefined);
const lastParams = ref(undefined);

watch(() => props.endpoint, value => {
  if (value) {
    loadData();
  }
}, {
  immediate: true
});

watch(() => props.params, value => {
  if (!value) {
    if (!lastParams.value) return;
    loadData();
    return;
  }
  let stringified = JSON.stringify(value);
  if (stringified !== lastParams.value) {
    loadData();
  }
}, {
  immediate: true
});

watch(() => props.reload, value => {
  if (value) loadData();
});

onMounted(() => {
  clearExpired();
});

function loadData() {
  const hash = props.cache ? (String(hashString(props.endpoint + ":" + JSON.stringify({ params: props.params ? props.params : {} })))) : undefined;
  if (props.cache) {
    let value = sessionStorage.getItem("HttpContent.cache.values." + hash);
    let expiration = sessionStorage.getItem("HttpContent.cache.expirations." + hash);
    if (value && expiration && parseInt(expiration) > Date.now()) {
      responseStatus.value = 200;
      responseData.value = value;
      return;
    }
  }
  emit("update:loading", true);
  lastParams.value = JSON.stringify(props.params);
  axios.get(props.endpoint, { params: props.params ? props.params : {} }).then(response => {
    emit("response", response.data);
    responseStatus.value = response.status;
    responseData.value = response.data;
    if (props.cache && responseStatus.value === 200) {
      sessionStorage.setItem("HttpContent.cache.values." + hash, JSON.stringify(responseData.value));
      sessionStorage.setItem("HttpContent.cache.expirations." + hash, String(Date.now() + (props.cache * 1000)));
      let list = JSON.parse(sessionStorage.getItem("HttpContent.cache.keys") ? sessionStorage.getItem("HttpContent.cache.keys") : "[]");
      if (!list.includes(hash)) list.push(hash);
      sessionStorage.setItem("HttpContent.cache.keys", JSON.stringify(list));
    }
  }).catch(reason => {
    responseStatus.value = reason.response?.status ?? 0;
  }).finally(() => {
    if (props.reload) emit("update:reload", false);
    emit("update:loading", false);
  });
}

function hashString(value) {
  var hash = 0, i, chr;
  if (value.length === 0) return hash;
  for (i = 0; i < value.length; i++) {
    chr   = value.charCodeAt(i);
    hash  = ((hash << 5) - hash) + chr;
    hash |= 0;
  }
  return hash;
}

function clearExpired() {
  const clearExpiresAt = sessionStorage.getItem("HttpContent.cache.clearExpiresAt");
  if (clearExpiresAt && parseInt(clearExpiresAt) > Date.now()) return;
  sessionStorage.setItem("HttpContent.cache.clearExpiresAt", String(Date.now() + 300000))
  let list = JSON.parse(sessionStorage.getItem("HttpContent.cache.keys") ? sessionStorage.getItem("HttpContent.cache.keys") : "[]");
  let deleted = [];
  list.forEach(hash => {
    let expiration = sessionStorage.getItem("HttpContent.cache.expirations." + hash);
    if (!expiration || parseInt(expiration) <= Date.now()) {
      deleted.push(hash);
      sessionStorage.removeItem("HttpContent.cache.values." + hash);
      sessionStorage.removeItem("HttpContent.cache.expirations." + hash);
    }
  });
  sessionStorage.setItem("HttpContent.cache.keys", JSON.stringify(list.filter(item => !deleted.includes(item))));
}
</script>